import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Monitors database connection health and refreshes session periodically
 * to prevent idle connection issues
 * 
 * @param enabled - Whether to enable health monitoring (default: true)
 * @param intervalMs - How often to check connection health (default: 5 minutes)
 */
export const useConnectionHealth = (enabled: boolean = true, intervalMs: number = 5 * 60 * 1000) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }
    
    /**
     * Performs a health check by refreshing the session
     */
    const performHealthCheck = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('[ConnectionHealth] Session check failed:', error);
          return;
        }
        
        if (!session) {
          return; // No session to maintain
        }
        
        // Check if session is close to expiring
        const expiresAt = session.expires_at;
        if (expiresAt) {
          const expiresIn = expiresAt * 1000 - Date.now();
          const refreshThreshold = 10 * 60 * 1000; // 10 minutes
          
          if (expiresIn < refreshThreshold) {
            console.log('[ConnectionHealth] Refreshing session proactively');
            await supabase.auth.refreshSession();
          }
        }
        
        // Perform a lightweight query to keep connection alive
        const { error: queryError } = await supabase
          .from('profiles')
          .select('id')
          .limit(0);
        
        if (queryError && !queryError.message.includes('relation')) {
          console.warn('[ConnectionHealth] Health check query failed:', queryError);
        } else {
          lastActivityRef.current = Date.now();
        }
      } catch (error) {
        console.error('[ConnectionHealth] Health check error:', error);
      }
    };
    
    // Perform initial health check
    performHealthCheck();
    
    // Set up periodic health checks
    intervalRef.current = setInterval(performHealthCheck, intervalMs);
    
    // Also refresh on user activity (visibility change, focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        // If app was hidden for more than 2 minutes, refresh session
        if (timeSinceLastActivity > 2 * 60 * 1000) {
          performHealthCheck();
        }
      }
    };
    
    const handleFocus = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > 2 * 60 * 1000) {
        performHealthCheck();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, intervalMs]);
};


